// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Request list component showing per-agent track status as cards.
 */

import { type FC, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight, Eye, Trash2 } from 'lucide-react'
import {
  Button,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui'
import type { Agent } from '@/subsystems/agents'
import type { SrmRequest } from '../types'
import { formatDate, trackPalette } from '../utils'

interface RequestListProps {
  requests: SrmRequest[]
  agents: Agent[]
  onSelectRequest?: (request: SrmRequest) => void
  onDeleteRequest?: (request: SrmRequest) => Promise<void>
}

const PAGE_SIZE = 10

interface TrackCount {
  filled: number
  total: number
}

function trackCountForAgent(request: SrmRequest, agent: Agent): TrackCount {
  const slotIds = new Set(agent.slots.map((s) => s.id))
  const filled = request.documents.filter((d) => d.slotId && slotIds.has(d.slotId)).length
  return { filled, total: agent.slots.length }
}

export const RequestList: FC<RequestListProps> = ({
  requests,
  agents,
  onSelectRequest,
  onDeleteRequest,
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [requestToDelete, setRequestToDelete] = useState<SrmRequest | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [page, setPage] = useState(0)

  // Sort by request number naturally (so SRM-9 < SRM-89 < SRM-90), newest first.
  const sortedRequests = useMemo(
    () =>
      [...requests].sort((a, b) =>
        b.groupId.localeCompare(a.groupId, undefined, { numeric: true, sensitivity: 'base' }),
      ),
    [requests],
  )

  const pageCount = Math.max(1, Math.ceil(sortedRequests.length / PAGE_SIZE))
  // Clamp instead of resetting via effect, so deletes that shrink the list stay in range.
  const currentPage = Math.min(page, pageCount - 1)
  const pageStart = currentPage * PAGE_SIZE
  const pageItems = sortedRequests.slice(pageStart, pageStart + PAGE_SIZE)

  const handleDeleteClick = (request: SrmRequest) => {
    setRequestToDelete(request)
    setDeleteDialogOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!requestToDelete || !onDeleteRequest) return
    setIsDeleting(true)
    try {
      await onDeleteRequest(requestToDelete)
    } finally {
      setIsDeleting(false)
      setDeleteDialogOpen(false)
      setRequestToDelete(null)
    }
  }

  if (requests.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="srm-empty-state">
        <p className="text-muted-foreground">
          No requests found. Click "New Request" to create one.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {pageItems.map((request) => (
        <div
          key={request.groupId}
          data-testid={`srm-card-${request.groupId}`}
          className="bg-card border rounded-lg p-4 hover:shadow-md transition-shadow"
        >
          <div className="flex flex-wrap items-start justify-between gap-2 mb-4">
            <div className="min-w-0">
              <h3 className="text-base sm:text-lg font-semibold text-foreground">
                {request.groupId}
                {request.externalRef && (
                  <span className="ml-2 text-sm font-normal text-muted-foreground">{request.externalRef}</span>
                )}
                {request.title && (
                  <span className="ml-2 text-sm font-normal text-muted-foreground">{request.title}</span>
                )}
              </h3>
            </div>
            <span className="text-xs sm:text-sm text-muted-foreground shrink-0">
              {request.uploadedBy && <>{request.uploadedBy} · </>}
              {formatDate(request.updatedAt)}
            </span>
          </div>

          {agents.length > 0 && (
            <div className="flex flex-wrap gap-3 mb-4">
              {agents.map((agent, idx) => {
                const palette = trackPalette(idx)
                const Icon = palette.Icon
                const { filled, total } = trackCountForAgent(request, agent)
                const hasAny = filled > 0
                const dotFill = palette.iconClass.replace('text-', 'bg-')

                return (
                  <div
                    key={agent.agentId}
                    data-testid={`badge-${agent.agentId}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md border ${palette.bgClass} ${palette.borderTintClass}`}
                  >
                    <Icon className={`h-4 w-4 ${palette.iconClass}`} />
                    <span className={`text-sm font-medium ${palette.iconClass}`}>{agent.label}</span>
                    {total > 0 && (
                      <div className="flex gap-0.5 ml-1">
                        {Array.from({ length: total }).map((_, i) => (
                          <span
                            key={i}
                            className={`w-2 h-2 rounded-full ${i < filled ? dotFill : 'bg-muted-foreground/30'}`}
                          />
                        ))}
                      </div>
                    )}
                    <span className="text-xs text-muted-foreground ml-1">
                      {total === 0 ? '' : hasAny ? `${filled}/${total}` : 'No docs'}
                    </span>
                  </div>
                )
              })}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2 border-t">
            {onDeleteRequest && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDeleteClick(request)}
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                aria-label="Delete request"
                data-testid={`srm-delete-${request.groupId}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="default"
              size="sm"
              onClick={() => onSelectRequest?.(request)}
              aria-label="View details"
              data-testid={`srm-view-${request.groupId}`}
            >
              <Eye className="h-4 w-4" />
              <span className="ml-1.5">View</span>
            </Button>
          </div>
        </div>
      ))}

      {sortedRequests.length > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-muted-foreground pt-2">
          <span>
            Showing {pageStart + 1}–{pageStart + pageItems.length} of {sortedRequests.length}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={currentPage === 0}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <span>
              Page {currentPage + 1} of {pageCount}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              disabled={currentPage >= pageCount - 1}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete request?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{requestToDelete?.groupId}" and all its documents.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting} data-testid="srm-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="srm-delete-confirm"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
